import React, { useState } from 'react';
import { Warehouse, Key, ChevronDown, ChevronUp, Building2, Calendar, DollarSign, AlertCircle } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const map = {
    occupied: 'bg-emerald-500/20 text-emerald-400',
    available: 'bg-sky-500/20 text-sky-400',
    maintenance: 'bg-amber-500/20 text-amber-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    terminated: 'bg-gray-500/20 text-gray-400',
    ground: 'bg-purple-500/20 text-purple-400',
    services_only: 'bg-indigo-500/20 text-indigo-400'
  };
  const cls = map[status?.toLowerCase()] || 'bg-gray-500/20 text-gray-400';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status || '—'}
    </span>
  );
};

const HangarTable = ({ hangars, onViewLeases }) => {
  const [sortField, setSortField] = useState('hangar_number');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedHangars = [...hangars].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (sortField === 'monthly_rent' || sortField === 'surface_sqft') {
      aVal = Number(aVal);
      bVal = Number(bVal);
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  return (
    <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <Warehouse size={15} className="text-yellow-400" />
          Hangars ({hangars.length})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-800/80">
            <tr className="text-gray-400 border-b border-gray-700">
              <th className="text-left py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('hangar_number')}>
                <div className="flex items-center gap-1">Hangar <SortIcon field="hangar_number" /></div>
              </th>
              <th className="text-left py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('name')}>
                Name <SortIcon field="name" />
              </th>
              <th className="text-left py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('surface_sqft')}>
                Surface <SortIcon field="surface_sqft" />
              </th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Current Tenant</th>
              <th className="text-right py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('monthly_rent')}>
                Rent <SortIcon field="monthly_rent" />
              </th>
              <th className="text-left py-2 px-3">Lease End</th>
              <th className="text-left py-2 px-3">Arrears</th>
              <th className="text-center py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {sortedHangars.length === 0 ? (
              <tr>
                <td colSpan="9" className="py-8 text-center text-gray-500">No hangars found</td>
              </tr>
            ) : (
              sortedHangars.map(h => (
                <tr key={h.id} className="hover:bg-gray-700/30 transition-colors">
                  <td className="py-2 px-3 font-mono text-gray-300">{h.hangar_number}</td>
                  <td className="py-2 px-3 text-gray-300">{h.name || '—'}</td>
                  <td className="py-2 px-3 text-gray-300">{h.surface_sqft?.toLocaleString()} sqft</td>
                  <td className="py-2 px-3"><StatusBadge status={h.status} /></td>
                  <td className="py-2 px-3 text-gray-300 max-w-[150px] truncate">
                    {h.current_tenant_name || (h.status === 'available' ? '—' : 'Unknown')}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-emerald-400">
                    ${Number(h.monthly_rent || 0).toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-gray-400">
                    {h.lease_end_date ? new Date(h.lease_end_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 px-3">
                    {h.rent_arrears > 0 ? (
                      <span className="text-red-400 flex items-center gap-1">
                        <AlertCircle size={10} /> ${h.rent_arrears.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-green-400">✓</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={() => onViewLeases(h.id, h.hangar_number)}
                      className="text-sky-400 hover:text-sky-300 transition-colors text-xs"
                    >
                      View Leases
                    </button>
                   </td>
                 </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LeaseTable = ({ leases }) => {
  const [sortField, setSortField] = useState('start_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedLeases = [...leases].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (sortField === 'monthly_rent') {
      aVal = Number(aVal);
      bVal = Number(bVal);
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  return (
    <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <Key size={15} className="text-blue-400" />
          Active Leases ({leases.length})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-800/80">
            <tr className="text-gray-400 border-b border-gray-700">
              <th className="text-left py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('lease_number')}>
                Lease # <SortIcon field="lease_number" />
              </th>
              <th className="text-left py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('tenant_name')}>
                Tenant <SortIcon field="tenant_name" />
              </th>
              <th className="text-left py-2 px-3">Hangar</th>
              <th className="text-left py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('start_date')}>
                Start Date <SortIcon field="start_date" />
              </th>
              <th className="text-left py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('end_date')}>
                End Date <SortIcon field="end_date" />
              </th>
              <th className="text-right py-2 px-3 cursor-pointer hover:text-white" onClick={() => handleSort('monthly_rent')}>
                Rent <SortIcon field="monthly_rent" />
              </th>
              <th className="text-left py-2 px-3">Type</th>
              <th className="text-left py-2 px-3">Insurance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {sortedLeases.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-8 text-center text-gray-500">No active leases</td>
              </tr>
            ) : (
              sortedLeases.map(lease => (
                <tr key={lease.id} className="hover:bg-gray-700/30 transition-colors">
                  <td className="py-2 px-3 font-mono text-gray-300">{lease.lease_number}</td>
                  <td className="py-2 px-3 text-gray-300 max-w-[150px] truncate">
                    {lease.tenant_name || '—'}
                  </td>
                  <td className="py-2 px-3 text-gray-300">{lease.hangar_number || '—'}</td>
                  <td className="py-2 px-3 text-gray-400">
                    {lease.start_date ? new Date(lease.start_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 px-3">
                    {lease.end_date ? (
                      <span className={new Date(lease.end_date) < new Date() ? 'text-red-400' : 'text-gray-400'}>
                        {new Date(lease.end_date).toLocaleDateString()}
                      </span>
                    ) : 'Open-ended'}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-emerald-400">
                    ${Number(lease.monthly_rent || 0).toLocaleString()}
                  </td>
                  <td className="py-2 px-3"><StatusBadge status={lease.lease_type || 'active'} /></td>
                  <td className="py-2 px-3">
                    {lease.insurance_expiry_date ? (
                      <span className={new Date(lease.insurance_expiry_date) < new Date() ? 'text-red-400 text-xs' : 'text-gray-400 text-xs'}>
                        {new Date(lease.insurance_expiry_date).toLocaleDateString()}
                      </span>
                    ) : '—'}
                  </td>
                 </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { HangarTable, LeaseTable };